/*
 * <rebuy-products> fetches recommendation products and renders them into
 * Dawn-compatible product card or compact add-on markup. Rebuy custom data
 * sources are used when a ruleset ID is present.
 */
if (!customElements.get('rebuy-products')) {
const REBUY_API_KEY = 'ab4d06bcdc1ff06c7be4f61dcb568d6c8e7c823d';
const REBUY_SESSION_STORAGE_KEY = 'uscRebuySessionUuid';
const trackedProductViews = new Set();

class RebuyProducts extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.rebuyMode || 'grid';
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.productId;
    this.productHandle = this.dataset.productHandle;
    this.customerId = this.dataset.customerId;
    this.dataSourceId = this.dataset.dataSourceId;
    this.apiKey = REBUY_API_KEY;
    this.sessionUuid = this.getSessionUuid();
    this.limit = parseInt(this.dataset.limit, 10) || 4;
    this.quickAdd = this.dataset.quickAdd || 'none';
    this.imageRatio = this.dataset.imageRatio || 'adapt';
    this.imageShape = this.dataset.imageShape || 'default';
    this.showSecondaryImage = this.dataset.showSecondaryImage === 'true';
    this.cardStyle = this.dataset.cardStyle || 'standard';
    this.cardColorScheme = this.dataset.cardColorScheme || 'scheme-3';
    this.badgePosition = this.dataset.badgePosition || 'bottom left';
    this.saleBadgeColorScheme = this.dataset.saleBadgeColorScheme || 'scheme-4';
    this.soldOutBadgeColorScheme = this.dataset.soldOutBadgeColorScheme || 'scheme-3';
    this.saleLabel = this.dataset.saleLabel || '';
    this.soldOutLabel = this.dataset.soldOutLabel || '';
    this.showMobileSlider = this.dataset.showMobileSlider === 'true';
    this.showDesktopSlider = this.dataset.showDesktopSlider === 'true';
    this.grid = this.querySelector('[data-rebuy-grid]');
    this.sliderButtons = this.querySelector('[data-rebuy-slider-buttons]');
    this.totalNode = this.querySelector('[data-rebuy-total]');
    this.trackProductView();

    if (this.mode === 'addon') {
      this.fetchAndRenderAddon();
      return;
    }

    if (this.mode === 'tabs') {
      this.fetchAndRenderTabs();
      return;
    }

    if (!this.dataSourceId) {
      console.warn('[rebuy-products] missing data-source-id; section will stay hidden.');
      return;
    }

    this.fetchAndRender();
  }

  buildRebuyRequestUrl(dataSourceId, limit = this.limit) {
    const params = new URLSearchParams({ key: this.apiKey });
    if (this.productId) params.set('shopify_product_ids', this.productId);
    if (this.customerId) params.set('shopify_customer_id', this.customerId);
    if (this.sessionUuid) params.set('uuid', this.sessionUuid);
    if (limit) params.set('limit', String(limit));
    return `https://rebuyengine.com/api/v1/custom/id/${encodeURIComponent(dataSourceId)}?${params}`;
  }

  getSessionUuid() {
    const rebuyUuid = this.getRebuyUuid();
    if (rebuyUuid) return rebuyUuid;

    if (window.__uscRebuySessionUuid) return window.__uscRebuySessionUuid;

    try {
      const existingUuid = window.sessionStorage?.getItem(REBUY_SESSION_STORAGE_KEY);
      if (existingUuid) {
        window.__uscRebuySessionUuid = existingUuid;
        return existingUuid;
      }

      const generatedUuid = this.createUuid();
      window.sessionStorage?.setItem(REBUY_SESSION_STORAGE_KEY, generatedUuid);
      window.__uscRebuySessionUuid = generatedUuid;
      return generatedUuid;
    } catch (err) {
      window.__uscRebuySessionUuid = this.createUuid();
      return window.__uscRebuySessionUuid;
    }
  }

  getRebuyUuid() {
    const candidates = [
      window.Rebuy?.user?.uuid,
      window.Rebuy?.customer?.uuid,
      window.Rebuy?.session?.uuid,
      window.Rebuy?.analytics?.uuid,
      window.Rebuy?.Analytics?.uuid,
    ];

    return candidates.find((uuid) => typeof uuid === 'string' && uuid.trim()) || '';
  }

  createUuid() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const random = Math.random() * 16 | 0;
      const value = char === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  isTrackingAllowed() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
        return privacy.analyticsProcessingAllowed();
      }
    } catch (err) {
      return true;
    }

    return true;
  }

  trackProductView() {
    if (!this.productId || !this.sessionUuid || !this.isTrackingAllowed()) return;

    const trackingKey = `${this.sessionUuid}:${this.productId}`;
    if (trackedProductViews.has(trackingKey)) return;
    trackedProductViews.add(trackingKey);

    const payload = {
      subject: 'user',
      verb: 'viewed',
      noun: 'product',
      uuid: this.sessionUuid,
      product_id: this.productId,
      platform: 'Shopify',
    };

    if (this.productHandle) payload.product_handle = this.productHandle;
    if (this.customerId) payload.customer_id = this.customerId;

    fetch('https://rebuyengine.com/api/v2/analytics/event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Rebuy-User-Token': this.apiKey,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  }

  async fetchProducts(dataSourceId, limit = this.limit) {
    if (!dataSourceId) {
      console.warn('[rebuy-products] missing data-source-id; section will stay hidden.');
      return [];
    }

    const url = this.buildRebuyRequestUrl(dataSourceId, limit);
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) throw new Error(`Recommendation fetch failed: ${res.status}`);

    const json = await res.json();
    const products = Array.isArray(json?.data) ? json.data : [];

    return products
      .map((product) => this.normalizeProduct(product))
      .filter((product) => product && !this.isBmsmOnlyProduct(product));
  }

  async fetchAndRender() {
    try {
      const products = await this.fetchProducts(this.dataSourceId, this.limit);
      if (!products.length) return;

      this.renderProducts(products);
      this.removeAttribute('hidden');
      this.refreshSlider(products.length);
    } catch (err) {
      console.error('[rebuy-products]', err);
    }
  }

  async fetchAndRenderAddon() {
    try {
      const products = await this.fetchProducts(this.dataSourceId, this.limit);
      if (!products.length) return;

      const list = this.querySelector('[data-rebuy-addon-list]');
      if (!list) return;

      const frag = document.createDocumentFragment();
      products.slice(0, this.limit).forEach((product) => {
        const item = document.createElement('div');
        item.className = 'product__rebuy-add-on-item';
        item.innerHTML = this.addOnHtml(product);
        frag.appendChild(item);
      });

      list.innerHTML = '';
      list.appendChild(frag);
      this.removeAttribute('hidden');
    } catch (err) {
      console.error('[rebuy-products]', err);
    }
  }

  async fetchAndRenderTabs() {
    const tabConfigs = [
      {
        key: 'recent',
        dataSourceId: this.dataset.recentDataSourceId || this.dataSourceId,
      },
      {
        key: 'best',
        dataSourceId: this.dataset.bestDataSourceId || this.dataSourceId,
      },
    ];

    try {
      const results = await Promise.all(
        tabConfigs.map(async (config) => {
          try {
            const products = await this.fetchProducts(config.dataSourceId, this.limit);
            return { ...config, products };
          } catch (err) {
            console.error('[rebuy-products]', err);
            return { ...config, products: [] };
          }
        })
      );

      const populated = results.filter((result) => result.products.length);
      if (!populated.length) return;

      populated.forEach((result) => {
        this.renderProducts(result.products, result.key);
        this.refreshSlider(result.products.length, result.key);
      });

      this.syncTabs(populated.map((result) => result.key));
      this.removeAttribute('hidden');
    } catch (err) {
      console.error('[rebuy-products]', err);
    }
  }

  renderProducts(products, key = null) {
    const grid = key ? this.querySelector(`[data-rebuy-grid="${key}"]`) : this.grid;
    if (!grid) return;

    const showSlider = this.showMobileSlider || this.showDesktopSlider;
    const frag = document.createDocumentFragment();

    products.forEach((product, index) => {
      const li = document.createElement('li');
      li.id = `Slide-${this.sectionId}-${key ? `${key}-` : ''}${index + 1}`;
      li.className = 'grid__item' + (showSlider ? ' slider__slide' : '');
      li.innerHTML = this.cardHtml(product);
      frag.appendChild(li);
    });

    grid.innerHTML = '';
    grid.appendChild(frag);
    this.resetSlider(grid);
  }

  refreshSlider(count, key = null) {
    const totalNode = key ? this.querySelector(`[data-rebuy-total="${key}"]`) : this.totalNode;
    const sliderButtons = key ? this.querySelector(`[data-rebuy-slider-buttons="${key}"]`) : this.sliderButtons;

    if (totalNode) totalNode.textContent = String(count);
    if (sliderButtons) sliderButtons.removeAttribute('hidden');
  }

  resetSlider(grid) {
    const sliderComponent = grid.closest('slider-component');
    if (!sliderComponent || typeof sliderComponent.resetPages !== 'function') return;

    requestAnimationFrame(() => {
      sliderComponent.resetPages();
    });
  }

  syncTabs(populatedKeys) {
    const tabs = Array.from(this.querySelectorAll('[data-rebuy-tab]'));
    const panels = Array.from(this.querySelectorAll('[data-rebuy-panel]'));
    const firstKey = populatedKeys[0];

    tabs.forEach((tab) => {
      const key = tab.dataset.rebuyTab;
      const isPopulated = populatedKeys.includes(key);
      tab.hidden = !isPopulated;
      tab.setAttribute('aria-selected', key === firstKey ? 'true' : 'false');
      tab.tabIndex = key === firstKey ? 0 : -1;
      tab.addEventListener('click', () => this.activateTab(key));
      tab.addEventListener('keydown', (event) => this.onTabKeydown(event, populatedKeys));
    });

    panels.forEach((panel) => {
      const key = panel.dataset.rebuyPanel;
      panel.hidden = key !== firstKey;
    });
  }

  activateTab(activeKey) {
    this.querySelectorAll('[data-rebuy-tab]').forEach((tab) => {
      const isActive = tab.dataset.rebuyTab === activeKey;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
      if (isActive) tab.focus();
    });

    this.querySelectorAll('[data-rebuy-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.rebuyPanel !== activeKey;
    });
  }

  onTabKeydown(event, populatedKeys) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    const currentKey = event.currentTarget.dataset.rebuyTab;
    const currentIndex = populatedKeys.indexOf(currentKey);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowLeft') nextIndex = currentIndex <= 0 ? populatedKeys.length - 1 : currentIndex - 1;
    if (event.key === 'ArrowRight') nextIndex = currentIndex >= populatedKeys.length - 1 ? 0 : currentIndex + 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = populatedKeys.length - 1;

    this.activateTab(populatedKeys[nextIndex]);
  }

  cardHtml(product) {
    const productUrl = product.url || `/products/${product.handle}`;
    const firstVariant = (product.variants && product.variants[0]) || null;
    const variantId = firstVariant?.id;
    const variantsCount = Array.isArray(product.variants) ? product.variants.length : 0;
    const available = this.isProductAvailable(product);
    const image = this.pickImage(product);
    const ratioClass = this.imageRatio === 'adapt' ? '' : ` ratio--${this.imageRatio}`;
    const aspectRatio = this.aspectRatioFor(image);

    const priceHtml = this.priceHtml(product);
    const buttonHtml = this.buttonHtml({
      productUrl,
      variantId,
      variantsCount,
      available,
      productTitle: product.title,
      buttonClass: 'quick-add__submit button button--full-width button--secondary',
      linkClass: 'button button--full-width button--secondary',
    });
    const cardStyle = this.cardStyle === 'card' ? 'card' : 'standard';
    const outerCardClasses = [
      'card',
      `card--${cardStyle}`,
      image ? 'card--media' : 'card--text',
      cardStyle === 'card' ? `color-${this.cardColorScheme} gradient` : '',
      this.imageShape && this.imageShape !== 'default' ? 'card--shape' : '',
      'card--extend-height',
      !image && cardStyle === 'card' ? 'ratio' : '',
    ].filter(Boolean).join(' ');
    const innerCardClasses = [
      'card__inner',
      cardStyle === 'standard' ? `color-${this.cardColorScheme} gradient` : '',
      image || cardStyle === 'standard' ? 'ratio' : '',
    ].filter(Boolean).join(' ');
    const mediaShapeClasses = this.imageShape && this.imageShape !== 'default'
      ? ` shape--${this.escape(this.imageShape)} color-${this.escape(this.cardColorScheme)} gradient`
      : '';
    const badgeHtml = this.badgeHtml(product);
    const imageHtml = image
      ? `<img
            src="${this.escape(image.src)}"
            alt="${this.escape(image.alt || product.title)}"
            class="motion-reduce"
            loading="lazy"
            width="${image.width || 533}"
            height="${image.height || Math.round((image.width || 533) / (aspectRatio || 1))}"
          >`
      : '';

    return `
      <div class="card-wrapper product-card-wrapper underline-links-hover" data-rebuy-card>
        <div class="${this.escape(outerCardClasses)}${ratioClass}" tabindex="-1" style="--ratio-percent: ${this.ratioPercent(aspectRatio)};">
          <div class="${this.escape(innerCardClasses)}" style="--ratio-percent: ${this.ratioPercent(aspectRatio)};">
            ${
              image
                ? `<div class="card__media${mediaShapeClasses}">
                    <div class="media media--transparent media--hover-effect">
                      ${imageHtml}
                    </div>
                  </div>`
                : ''
            }
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="${this.escape(productUrl)}" class="full-unstyled-link">${this.escape(product.title)}</a>
                </h3>
              </div>
              <div class="card__badge ${this.escape(this.badgePosition)}">${badgeHtml}</div>
            </div>
          </div>
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading h5">
                <a href="${this.escape(productUrl)}" class="full-unstyled-link">${this.escape(product.title)}</a>
              </h3>
              <div class="card-information">
                ${priceHtml}
              </div>
            </div>
            ${buttonHtml}
            <div class="card__badge ${this.escape(this.badgePosition)}">${badgeHtml}</div>
          </div>
        </div>
      </div>
    `;
  }

  addOnHtml(product) {
    const productUrl = product.url || `/products/${product.handle}`;
    const firstVariant = (product.variants && product.variants[0]) || null;
    const image = this.pickImage(product);
    const imageHtml = image
      ? `<img
          src="${this.escape(image.src)}"
          alt="${this.escape(image.alt || product.title)}"
          loading="lazy"
          width="${image.width || 96}"
          height="${image.height || 96}"
        >`
      : '';

    return `
      <a class="product__rebuy-add-on-media" href="${this.escape(productUrl)}" aria-label="${this.escape(product.title)}">
        ${imageHtml}
      </a>
      <div class="product__rebuy-add-on-copy">
        <h3 class="product__rebuy-add-on-title">
          <a href="${this.escape(productUrl)}">${this.escape(product.title)}</a>
        </h3>
        ${this.priceHtml(product)}
      </div>
      <div class="product__rebuy-add-on-action">
        ${this.buttonHtml({
          productUrl,
          variantId: firstVariant?.id,
          variantsCount: Array.isArray(product.variants) ? product.variants.length : 0,
          available: this.isProductAvailable(product),
          productTitle: product.title,
          buttonClass: 'button button--secondary product__rebuy-add-on-button',
          linkClass: 'button button--secondary product__rebuy-add-on-button',
          wrapAction: false,
        })}
      </div>
    `;
  }

  buttonHtml({ productUrl, variantId, variantsCount, available, productTitle, buttonClass, linkClass, wrapAction = true }) {
    const isQuickAddable = this.quickAdd !== 'none' && available && variantsCount === 1 && variantId;
    if (isQuickAddable) {
      const formHtml = `<product-form data-section-id="${this.escape(this.sectionId)}">
        <form
          action="/cart/add"
          method="post"
          enctype="multipart/form-data"
          data-type="add-to-cart-form"
          novalidate
        >
          <input type="hidden" name="form_type" value="product">
          <input type="hidden" name="utf8" value="✓">
          <input type="hidden" name="id" value="${this.escape(variantId)}">
          <input type="hidden" name="quantity" value="1">
          <button
            type="submit"
            name="add"
            class="${this.escape(buttonClass)}"
            aria-label="${this.escape(productTitle)}"
          >
            <span>${this.escape(window.variantStrings?.addToCart || 'Add to cart')}</span>
            <div class="loading__spinner hidden">
              <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            </div>
          </button>
        </form>
      </product-form>`;

      return wrapAction ? `<div class="quick-add no-js-hidden" data-rebuy-quick-add>${formHtml}</div>` : formHtml;
    }

    const label =
      variantsCount > 1
        ? window.accessibilityStrings?.chooseOptions || 'Choose options'
        : window.variantStrings?.unavailable || 'View product';

    const linkHtml = `<a
      href="${this.escape(productUrl)}"
      class="${this.escape(linkClass)}"
      aria-label="${this.escape(productTitle)}"
    >
      <span>${this.escape(label)}</span>
    </a>`;

    return wrapAction ? `<div class="quick-add no-js-hidden" data-rebuy-quick-add>${linkHtml}</div>` : linkHtml;
  }

  normalizeProduct(product) {
    if (!product || !product.handle) return null;
    return {
      ...product,
      url: product.url || `/products/${product.handle}`,
      images: this.normalizeImages(product),
      variants: Array.isArray(product.variants) ? product.variants : [],
    };
  }

  isBmsmOnlyProduct(product) {
    const tags = Array.isArray(product?.tags)
      ? product.tags
      : String(product?.tags || '').split(',');

    return tags.some((tag) => String(tag).trim().toLowerCase() === 'bmsm-only');
  }

  normalizeImages(product) {
    const rawImages = [];
    if (product.image) rawImages.push(product.image);
    if (product.featured_image) rawImages.push(product.featured_image);
    if (Array.isArray(product.images)) rawImages.push(...product.images);
    if (Array.isArray(product.media)) {
      rawImages.push(...product.media.map((media) => media.preview_image || media.src || media));
    }

    return rawImages
      .map((image) => {
        if (!image) return null;
        if (typeof image === 'string') return { src: this.normalizeImageSrc(image), alt: product.title };
        const src = this.normalizeImageSrc(
          image.src || image.url || image.original_src || image.preview_image?.src || image.preview_image?.url
        );
        if (!src) return null;
        return {
          src,
          alt: image.alt || product.title,
          width: image.width,
          height: image.height,
        };
      })
      .filter(Boolean);
  }

  normalizeImageSrc(src) {
    if (!src || typeof src !== 'string') return null;
    if (src.startsWith('//')) return `${window.location.protocol}${src}`;
    return src;
  }

  pickImage(product) {
    const images = product.images || [];
    if (!images.length) return null;
    return images[0];
  }

  isProductAvailable(product) {
    if (typeof product.available === 'boolean') return product.available;
    if (!Array.isArray(product.variants) || !product.variants.length) return false;
    return product.variants.some((variant) => {
      if (typeof variant.available === 'boolean') return variant.available;
      return variant.inventory_quantity > 0 || variant.inventory_policy === 'continue' || variant.inventory_management == null;
    });
  }

  badgeHtml(product) {
    const available = this.isProductAvailable(product);
    const firstVariant = (product.variants && product.variants[0]) || null;
    const price = this.parsePrice(firstVariant?.price ?? product.price ?? product.price_min);
    const compare = this.parsePrice(firstVariant?.compare_at_price ?? product.compare_at_price ?? product.compare_at_price_min);

    if (!available) {
      return this.soldOutLabel
        ? `<span class="badge badge--bottom-left color-${this.escape(this.soldOutBadgeColorScheme)}">${this.escape(this.soldOutLabel)}</span>`
        : '';
    }

    if (compare && price && compare > price) {
      return this.saleLabel
        ? `<span class="badge badge--bottom-left color-${this.escape(this.saleBadgeColorScheme)}">${this.escape(this.saleLabel)}</span>`
        : '';
    }

    return '';
  }

  aspectRatioFor(image) {
    if (this.imageRatio === 'square') return 1;
    if (this.imageRatio === 'portrait') return 0.8;
    if (!image) return 1;
    if (image.width && image.height) return image.width / image.height;
    return 1;
  }

  ratioPercent(aspect) {
    if (!aspect || aspect <= 0) return '100%';
    return `${(1 / aspect) * 100}%`;
  }

  priceHtml(product) {
    const firstVariant = (product.variants && product.variants[0]) || null;
    const priceValue = firstVariant?.price ?? product.price ?? product.price_min;
    if (priceValue == null) return '';

    const price = this.parsePrice(priceValue);
    const compareValue = firstVariant?.compare_at_price ?? product.compare_at_price ?? product.compare_at_price_min;
    const compare = this.parsePrice(compareValue);
    const formatted = this.money(price);
    const onSale = compare && compare > price;

    if (onSale) {
      return `<div class="price price--on-sale">
        <div class="price__container">
          <div class="price__sale">
            <s class="price-item price-item--regular">${this.money(compare)}</s>
            <span class="price-item price-item--sale price-item--last">${formatted}</span>
          </div>
        </div>
      </div>`;
    }

    return `<div class="price">
      <div class="price__container">
        <div class="price__regular">
          <span class="price-item price-item--regular">${formatted}</span>
        </div>
      </div>
    </div>`;
  }

  parsePrice(value) {
    if (value == null || value === '') return null;
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (Number.isNaN(num)) return null;
    if (Number.isInteger(num) && num >= 1000) return num;
    return Math.round(num * 100);
  }

  money(cents) {
    if (cents == null) return '';
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && window.Shopify.money_format) {
      try {
        return Shopify.formatMoney(cents, window.Shopify.money_format);
      } catch (e) {
        /* fall through */
      }
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  escape(value) {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

  customElements.define('rebuy-products', RebuyProducts);
}
